import React, {useEffect, useRef, useState} from "react";
import {SoButton} from "../../UI/Button/so-button";
import "./sign-document.css";
import {DocusealForm} from "@docuseal/react";
import {addToUserforms} from "../../../Actions/authActions";
import {useDispatch} from "react-redux";
import {captureError} from "../../../utils";

class DocusealErrorBoundary extends React.Component {
    state = { hasError: false, retryCount: 0 };
    static getDerivedStateFromError() { return { hasError: true }; }
    componentDidCatch(error) {
        captureError(error, { errorType: 'docuseal_render_error', context: { retryCount: this.state.retryCount } });
    }
    render() {
        if (this.state.hasError && this.state.retryCount < 2) {
            // Auto-retry once after a brief delay to let the custom element fully initialize
            setTimeout(() => this.setState(s => ({ hasError: false, retryCount: s.retryCount + 1 })), 500);
            return <div style={{padding: '20px', textAlign: 'center'}}>Loading document form...</div>;
        }
        if (this.state.hasError) {
            return <div style={{padding: '20px', color: '#c00'}}>Unable to load the document form. Please refresh the page.</div>;
        }
        return this.props.children;
    }
}

export const SignDocument = ({doc, close, subdomain, sosCustomerId, userId, userEmail}) => {
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scriptReady, setScriptReady] = useState(false);
    const dispatch = useDispatch();
    const mountedRef = useRef(true);
    const formContainerRef = useRef(null);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
            try {
                const el = formContainerRef.current?.querySelector('docuseal-form');
                if (el) el.remove();
            } catch (e) { /* ignore */ }
        };
    }, []);

    useEffect(() => {
        const scriptId = 'docuseal-form-script';
        const scriptSrc = 'https://docuseal.sosoftpay.com/js/form.js';

        const onReady = () => {
            if (mountedRef.current) setScriptReady(true);
        };

        let existing = document.getElementById(scriptId);
        if (!existing) {
            existing = document.createElement('script');
            existing.id = scriptId;
            existing.async = true;
            existing.src = scriptSrc;
            document.head.appendChild(existing);
        }

        if (customElements.get('docuseal-form')) {
            // Custom element is registered but give it a tick to fully initialize internal refs
            requestAnimationFrame(() => onReady());
        } else {
            existing.addEventListener('error', () => {
                if (!mountedRef.current) return;
                const err = new Error('Docuseal form script failed to load');
                err.src = scriptSrc;
                err.docSlug = doc?.slug;
                err.subdomain = subdomain;
                captureError(err, {
                    errorType: 'docuseal_script_load_error',
                    context: {
                        src: scriptSrc,
                        docSlug: doc?.slug,
                        subdomain,
                        userId,
                        sosCustomerId,
                    },
                });
                setError('Unable to load the document signing form. Please refresh the page to try again.');
                setLoading(false);
            });

            // Wait for custom element to be defined, then give it a tick to initialize internal Vue refs
            customElements.whenDefined('docuseal-form').then(() => {
                requestAnimationFrame(() => onReady());
            });
        }

        const timeout = setTimeout(() => {
            if (loading && mountedRef.current) {
                const err = new Error('Docuseal form load timeout after 30s');
                err.docSlug = doc?.slug;
                err.subdomain = subdomain;
                captureError(err, {
                    errorType: 'docuseal_load_timeout',
                    context: {
                        docSlug: doc?.slug,
                        subdomain,
                        userId,
                        sosCustomerId,
                        scriptExists: !!document.getElementById(scriptId),
                    },
                });
                setError('The document form is taking too long to load. Please check your connection and try again.');
                setLoading(false);
            }
        }, 30000);

        return () => clearTimeout(timeout);
    }, []);

    useEffect(() => {
        if (data && data.submission_id) {
            dispatch(addToUserforms({
                userFormData: data,
                subdomain, intakeformId: doc._id, sosCustomerId, userId,
                originalname: doc.originalname
            }, subdomain, doc._id));
            close();
        }
    }, [data]);

    return (
        <div className={'prepare-document-container'}>
            <div className={'header'}>
                <h4>Signing {doc.originalname.slice(0, 20)}</h4>
                <div className={'header-action'}>
                    <div>
                        <button
                            className="sos-custom-btn-new-message sos-secondary-btn-css so-btn"
                            onClick={() => close()}>
                            Cancel
                        </button>
                    </div>
                    {/*<div>*/}
                    {/*    <SoButton onClick={() => upload()} text={'Submit'}></SoButton>*/}
                    {/*</div>*/}
                </div>
            </div>
            <div className={'document-container'}>
                <div className={'viewer-container'}>
                    <div className={'p-10'}>
                        Instructions: Fill in the required fields, then submit the document.
                    </div>

                    <div ref={formContainerRef}>
                        {scriptReady && <DocusealErrorBoundary><DocusealForm
                            host={'docuseal.sosoftpay.com'}
                            src={`https://docuseal.sosoftpay.com/d/${doc.slug}`}
                            orderAsOnPage={true}
                            expand={false}
                            goToLast={true}
                            sendCopyEmail={false}
                            withDownloadButton={false}
                            email={userEmail}
                            externalId={doc.slug + '_' + userId + '_' + (new Date()).toISOString()}
                            reuseSignature={false}
                            onLoad={() => {
                                if (mountedRef.current) setLoading(false);
                            }}
                            onComplete={async (data) => {
                                if (mountedRef.current) setData(data);
                            }}
                            withSendCopyButton={false}
                            customCss={`
                            div > div > div.flex.sticky.top-0.z-50.space-x-2.items-center.bg-yellow-100.p-2.border-y.border-yellow-200 {display: none;}
                            #scrollbox > div > div.mt-4.flex{display: none;}
                            #scrollbox {color: #000;}
                            #form_container {color: #000;}
                            div > div > div.max-w-md.mx-auto.px-2.mt-12.mb-4 > div > div.space-y-6 > div.text-center.w-full.space-y-4 {display: none;}
                            `}
                        /></DocusealErrorBoundary>}
                    </div>
                </div>
            </div>
            {error && <div className={'error'}>
                {error}
            </div>}
        </div>
    );
};
